import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { requestNotificationPermission, onForegroundMessage } from '@/services/firebase/messaging'

type PermissionStatus = NotificationPermission | 'unsupported'

/**
 * Hook that manages FCM push notification permission and foreground message handling.
 *
 * Usage: call once at app level (e.g. App.tsx) alongside useNotificationSubscription().
 */
export function usePushNotifications() {
  const user = useAuthStore((s) => s.user)
  const showToast = useUIStore((s) => s.showToast)

  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
    return Notification.permission
  })

  const isSupported = permissionStatus !== 'unsupported'

  // Request permission & register token
  const requestPermission = useCallback(async () => {
    if (!user || !isSupported) return null

    const token = await requestNotificationPermission(user.uid)
    setPermissionStatus(Notification.permission)

    if (token) {
      showToast('Push notifications enabled!', 'success')
    } else if (Notification.permission === 'denied') {
      showToast('Notifications blocked. Enable them in your browser settings.', 'warning')
    }

    return token
  }, [user, isSupported, showToast])

  // Set up foreground message listener when permission is granted
  useEffect(() => {
    if (!user || permissionStatus !== 'granted') return

    let cleanup: (() => void) | null = null

    onForegroundMessage((payload) => {
      const title = payload.notification?.title || 'iSQROLL'
      const body = payload.notification?.body || 'You have a new notification'
      showToast(`${title}: ${body}`, 'info')
    }).then((unsub) => {
      cleanup = unsub
    })

    return () => {
      cleanup?.()
    }
  }, [user, permissionStatus, showToast])

  return {
    permissionStatus,
    isSupported,
    requestPermission,
  }
}
